import { useCallback, useState } from "react";

/**
 * Custom hook to handle state that needs to be saved in local storage.
 * @param key Key to store the state under in local storage
 * @param defaultValue initial value of the useState value
 */
function useLocalStorageState<T>(
  key: string,
  defaultValue: T,
): [T, (newValue: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const setStoredValue = useCallback(
    (newValue: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved =
          newValue instanceof Function ? newValue(prev) : newValue;
        localStorage.setItem(key, JSON.stringify(resolved));
        return resolved;
      });
    },
    [key],
  );

  return [value, setStoredValue];
}

export default useLocalStorageState;
